import { ExtendLogo } from '@jouzen/ecom-components/icons/ExtendLogo';
import { Button } from '@jouzen/ecom-components/v3/Button';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { Modal } from '@jouzen/ecom-components/v4/Modal';
import { useGateValue } from '@statsig/react-bindings';
import { cx } from 'class-variance-authority';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import { EventType } from '@/analytics/types';
import useMultiRingSelections from '@/app/[locale]/product/_components/HardwarePDP/_hooks/useMultiRingSelections';
import usePDPExperiment from '@/app/[locale]/product/_components/HardwarePDP/_hooks/usePDPExperiment';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import RichText from '@/app/components/RichText';
import useUsaOnly from '@/app/hooks/useUsaOnly';

import {
  PDP_CONTROL,
  PDP_NEW_INTERSTITIAL,
  PDP_NEW_PREPAID_INTERSTITIAL,
  PDP_NEW_WARRANTY_INTERSTITIAL,
  PDP_PREPAID_INTERSTITIAL,
  PDP_WARRANTY_INTERSTITIAL,
} from '../_consts/pdpVariants';
import type { BuildHardwareOrderBaseProps } from '../_types/order';
import ExtendedWarrantySelections from './ExtendedWarrantySelections';
import ModalFooter from './ModalFooter';
import OneYearMembershipSelection from './OneYearMembershipSelection';

const tValue = (t: (val: MessageKey) => string) => ({
  logo: function logo() {
    return (
      <ExtendLogo
        aria-label={t('extend')}
        className="mb-1 ml-1 inline h-[16px] w-[75.2px]"
      />
    );
  },
});

interface AddOnModalProps
  extends Pick<
    BuildHardwareOrderBaseProps,
    'order' | 'twoYearWarranty' | 'threeYearWarranty' | 'yearlyMembership'
  > {
  readonly 'data-cy'?: string;
  readonly isAddingToCart: boolean;
  readonly isSelectingSize: boolean;
  readonly location: 'summary card' | 'sticky';
  readonly onAddToCart: () => Promise<void>;
  readonly onRadioChange: (value: string) => void;
  readonly ringCount: number;
}

const AddOnModal = ({
  'data-cy': dataCy = 'add-to-cart-button',
  isAddingToCart,
  isSelectingSize,
  location,
  onAddToCart,
  onRadioChange,
  order,
  ringCount,
  threeYearWarranty,
  twoYearWarranty,
  yearlyMembership,
}: AddOnModalProps) => {
  const hsaFsaGate = useGateValue('hsafsa_rollout');
  const isExtendedWarranty = useGateValue('extended_warranty');
  const variant = usePDPExperiment();
  const [proceedButtonText, setProceedButtonText] = useState<MessageKey>(
    'bundle_modal_skip_button',
  );
  const t = useTranslations();
  const path = usePathname();
  const isUS = useUsaOnly();
  const { membershipSelection, warrantySelection } = useMultiRingSelections();
  const existingMembershipValue = membershipSelection.value;
  const existingWarrantyValue = warrantySelection.value;
  const selectedWarranty = order.warranty.value as WarrantySelection;
  const selectedMembership = order.membership.value as MembershipSelection;

  const showMembership =
    variant !== PDP_WARRANTY_INTERSTITIAL &&
    variant !== PDP_NEW_WARRANTY_INTERSTITIAL;

  const isControlOrVariantOne =
    variant === PDP_CONTROL || variant === PDP_NEW_INTERSTITIAL;
  const warrantyVariant = isControlOrVariantOne ? 'extra-large' : 'large';

  const showWarranty =
    variant !== PDP_PREPAID_INTERSTITIAL &&
    variant !== PDP_NEW_PREPAID_INTERSTITIAL &&
    isExtendedWarranty;

  const disabled = isAddingToCart || isSelectingSize;
  const isDefaultMultiRingSelection =
    existingMembershipValue === selectedMembership &&
    existingWarrantyValue === selectedWarranty;

  // analytics for modal open
  const handleChange = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'add_to_cart',
      action: 'open_bundle_modal',
      path,
      location,
    });
  };

  // handle multiring footer cta text
  useEffect(() => {
    if (ringCount > 0 && !isAddingToCart) {
      if (isDefaultMultiRingSelection) {
        setProceedButtonText('pdp_add_to_cart');
      } else {
        setProceedButtonText('bundle_cta_update_cart');
      }
    }
  }, [isAddingToCart, isDefaultMultiRingSelection, ringCount]);

  // handle single ring footer cta text and radio change
  const handleRadioChange = (
    value: MembershipSelection | WarrantySelection,
  ) => {
    if (ringCount === 0) {
      if (
        (value === MembershipSelection.OneMonth &&
          selectedWarranty === WarrantySelection.OneYear) ||
        (value === WarrantySelection.OneYear &&
          selectedMembership === MembershipSelection.OneMonth)
      ) {
        setProceedButtonText('bundle_modal_skip_button');
      } else {
        setProceedButtonText('pdp_add_to_cart');
      }
    }
    onRadioChange(value);
  };

  return (
    <Modal
      onOpenChange={handleChange}
      triggerButton={(props) => (
        <Button
          {...props}
          className="w-full max-lg:text-sm"
          data-cy={dataCy}
          disabled={disabled}
          loading={isAddingToCart}
          loadingTextEnabled
          size="large"
          variant="primary"
        >
          {t('pdp_add_to_cart')}
        </Button>
      )}
      variant={warrantyVariant}
    >
      {({ titleProps }) => (
        <div>
          <div className="border-b border-gray-300 pb-6 md:pb-8">
            <Typography
              className="text-2xl font-medium"
              data-cy="bundle_title"
              id={titleProps.id!}
            >
              {t('bundle_modal_title')}
            </Typography>
            {isControlOrVariantOne && (
              <Typography
                className="mt-3"
                data-cy={hsaFsaGate ? 'hsa_fsa_subtitle' : 'default_subtitle'}
              >
                {t(
                  hsaFsaGate
                    ? 'bundle_modal_subtext_hsa_fsa'
                    : 'bundle_modal_subtext',
                )}
              </Typography>
            )}
          </div>
          <div
            className={cx('grid grid-cols-1 px-2 py-6 md:gap-x-8 xl:gap-x-14', {
              'md:grid-cols-2': isControlOrVariantOne,
            })}
          >
            {showMembership && (
              <div className="row-start-1">
                <Typography
                  data-cy="one-year-title"
                  height="loose"
                  variant="h6"
                >
                  {t('bundle_modal_one_year_title')}
                </Typography>
                <Typography
                  className="mb-5"
                  data-cy="bundle-membership-subtitle"
                  variant="body-small"
                >
                  {t(
                    isUS
                      ? 'bundle_modal_one_year_subtitle_hsa'
                      : 'bundle_modal_one_year_subtitle',
                  )}
                </Typography>
              </div>
            )}
            {showMembership && (
              <OneYearMembershipSelection
                onMembershipSelection={handleRadioChange}
                order={order}
                variant={variant}
                yearlyMembership={yearlyMembership}
              />
            )}
            {showWarranty && (
              <div className="row-start-3 mt-6 md:row-start-1 md:mt-0">
                <Typography color="dark" height="loose" variant="h6">
                  <RichText
                    messageKey="pdp_warranty_step_title_new"
                    tValues={tValue(t)}
                  />
                </Typography>
                <Typography className="mb-5" color="dark" variant="body-small">
                  {t(
                    hsaFsaGate
                      ? 'bundle_modal_warranty_subtext_hsa_fsa'
                      : 'bundle_modal_warranty_subtitle',
                  )}
                </Typography>
              </div>
            )}
            {showWarranty && (
              <ExtendedWarrantySelections
                onWarrantySelection={handleRadioChange}
                order={order}
                threeYearWarranty={threeYearWarranty}
                twoYearWarranty={twoYearWarranty}
                variant={variant}
              />
            )}
          </div>
          <ModalFooter
            isAddingToCart={isAddingToCart}
            onAddToCart={onAddToCart}
            order={order}
            proceedButtonText={proceedButtonText}
          />
        </div>
      )}
    </Modal>
  );
};

export default AddOnModal;
